import { useStripe } from '@stripe/react-stripe-js'
import {
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
} from '@stripe/stripe-js'
import { useMutation } from 'react-query'
import { useToast } from '@chakra-ui/toast'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { fetchAPI } from '@/lib/api'
import { isAxiosError, getErrorMsg } from '@/lib/helpers'
import { logEvent, logEcommerce } from '@/lib/stats'
import {
  useCreateCreditCard,
  useSetupIntent,
} from '@/features/payments/mutations'

import { FreeTrialFormInput, FreeTrialSubscriptionResult } from '../types'
import { updatedPriceConfig } from '../../payments/config'

import { ERROR_TOAST_CONFIG } from '@/lib/constants'

type Payload = {
  data: {
    form: FreeTrialFormInput & { payment_type?: 'manual' | 'credit_card' }
    number: StripeCardNumberElement | null
    expiry: StripeCardExpiryElement | null
    cvc: StripeCardCvcElement | null
  }
}

export const useSubmitFreeTrial = () => {
  const stripe = useStripe()

  const toast = useToast()

  const setupIntentMutation = useSetupIntent()
  const createCreditCardMutation = useCreateCreditCard()

  const { t } = useTranslation('payment')

  return useMutation(
    async ({ data }: Payload) => {
      const { form, number, expiry, cvc } = data
      const isManual = form.payment_type === 'manual'

      let paymentMethodId: string | null = null

      if (!isManual) {
        if (!stripe || !number || !expiry || !cvc) {
          throw new Error(t('toast.failed'))
        }

        const clientSecret = await setupIntentMutation.mutateAsync(null)

        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: number,
            billing_details: {
              name: form.name,
            },
          },
        })

        if (result.error) {
          throw new Error(result.error.message)
        }

        if (!result.setupIntent.payment_method) {
          throw new Error(t('toast.paymentFailed'))
        }

        paymentMethodId = result.setupIntent.payment_method

        await createCreditCardMutation.mutateAsync({
          data: {
            payment_method_id: paymentMethodId,
            default_card: true,
          },
        })
     }

      return await fetchAPI<FreeTrialSubscriptionResult>({
        method: 'post',
        path: '/packages/free_trial',
        data: {
          details: form.address,
          name: form.name,
          package: form.package,
          payment_method_id: paymentMethodId,
          recurring: form.recurring,
          taxpayer_id: form.tax_id,
          taxpayer_type: form.taxpayer_type,
          code: form.code,
          email: form.email,
          phone_number: form.phone,
          tax_id_type: form.tax_id_type,
          branch_type: form.branch_type,
          branch_name: form.branch_name,
          sub_district: form.sub_district,
          district: form.district,
          province: form.province,
          zip_code: form.zip_code,
          payment_type: form.payment_type,
        },
      })
    },
    {
      onSuccess(result, payload) {
        const { form } = payload.data

        const predictedLTV =
          form.recurring === 'year'
            ? updatedPriceConfig[form.package].price.year
            : updatedPriceConfig[form.package].price.month * 12

        const value = updatedPriceConfig[form.package].price[form.recurring]
        logEvent({
          ga: {
            category: 'Free Trial Success',
            action: `${form.package} ${form.recurring}`,
            label: `${value}`,
            value,
          },
          fb: {
            event: 'StartTrial',
            value: '0',
            currency: 'THB',
            predicted_ltv: `${predictedLTV}.00`,
          },
        })

        logEcommerce('purchase', {
          value,
          items: [
            {
              item_id: `${form.package}_${form.recurring}`,
              item_name: `${form.package}_${form.recurring}`,
              price: value,
              quantity: 1,
            },
          ],
        })

        if (result.data.invoice.token) {
          window.location.href = `/payments/${result.data.invoice.token}/success?type=free-trial&package=${result.data.package}`
        } else {
          window.location.href = '/payments/bank_transfer_success?type=free-trial'
        }
      },
      onError(error: Error | AxiosError) {
        if (isAxiosError(error)) {
          toast({
            ...ERROR_TOAST_CONFIG,
            description: getErrorMsg(error),
          })
        } else {
          toast({
            ...ERROR_TOAST_CONFIG,
            description: error.message,
          })
        }
      },
    },
  )
}
